import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { 
  HelpCircle, Zap, CreditCard, Building, Smartphone, MapPin, Users, Clock, 
  FileText, DollarSign, BarChart3, Camera, Calendar, Bell, Bot, RefreshCw, 
  HardDrive, CloudSun, Settings, Shield, Wrench, Package, Briefcase, 
  AlertTriangle, CheckCircle, Home, Globe, Layers, Monitor, Server
} from 'lucide-react';

// Available icons for section headers
const AVAILABLE_ICONS = [
  { name: 'HelpCircle', icon: HelpCircle, label: 'Help' },
  { name: 'Zap', icon: Zap, label: 'Getting Started' },
  { name: 'CreditCard', icon: CreditCard, label: 'Billing' },
  { name: 'Building', icon: Building, label: 'Admin' },
  { name: 'Smartphone', icon: Smartphone, label: 'Mobile' },
  { name: 'MapPin', icon: MapPin, label: 'Location' },
  { name: 'Users', icon: Users, label: 'Team' },
  { name: 'Clock', icon: Clock, label: 'Time' },
  { name: 'FileText', icon: FileText, label: 'Tasks' },
  { name: 'DollarSign', icon: DollarSign, label: 'Money' },
  { name: 'BarChart3', icon: BarChart3, label: 'Reports' },
  { name: 'Camera', icon: Camera, label: 'Photos' },
  { name: 'Calendar', icon: Calendar, label: 'Calendar' },
  { name: 'Bell', icon: Bell, label: 'Notifications' },
  { name: 'Bot', icon: Bot, label: 'AI' },
  { name: 'RefreshCw', icon: RefreshCw, label: 'Sync' },
  { name: 'HardDrive', icon: HardDrive, label: 'Storage' },
  { name: 'CloudSun', icon: CloudSun, label: 'Weather' },
  { name: 'Settings', icon: Settings, label: 'Settings' },
  { name: 'Shield', icon: Shield, label: 'Security' },
  { name: 'Wrench', icon: Wrench, label: 'Tools' },
  { name: 'Package', icon: Package, label: 'Materials' },
  { name: 'Briefcase', icon: Briefcase, label: 'Work' },
  { name: 'AlertTriangle', icon: AlertTriangle, label: 'Warning' },
  { name: 'CheckCircle', icon: CheckCircle, label: 'Complete' },
  { name: 'Home', icon: Home, label: 'Home' },
  { name: 'Globe', icon: Globe, label: 'Web' },
  { name: 'Layers', icon: Layers, label: 'Layers' },
  { name: 'Monitor', icon: Monitor, label: 'Platform' },
  { name: 'Server', icon: Server, label: 'System' },
];

// Helper to get icon component by name
const getIconByName = (iconName) => {
  const found = AVAILABLE_ICONS.find(i => i.name === iconName);
  return found ? found.icon : HelpCircle;
};

const FAQManagementPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Editing states
  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Track focused/selected row for insert position
  const [focusedRowId, setFocusedRowId] = useState(null);
  
  // Track collapsed sections
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ 
    show: false, 
    faqId: null, 
    faqName: '', 
    isDeleting: false,
    isSection: false,
    childCount: 0
  });
  
  // Move to section modal state
  const [moveModal, setMoveModal] = useState({
    show: false,
    faqId: null,
    targetSection: '',
    isMoving: false
  });
  
  // Icon picker modal state
  const [iconModal, setIconModal] = useState({
    show: false,
    sectionId: null,
    currentIcon: null
  });
  
  // Open icon picker for a section
  const openIconPicker = (sectionId, currentIcon) => {
    setIconModal({ show: true, sectionId, currentIcon });
  };
  
  // Save selected icon for a section
  const saveIcon = async (iconName) => {
    if (!iconModal.sectionId) return;
    
    // Update local state
    setFaqs(prev => prev.map(faq => 
      faq.id === iconModal.sectionId ? { ...faq, icon: iconName } : faq
    ));
    
    // Save to backend
    await autoSave(iconModal.sectionId, { icon: iconName });
    
    setIconModal({ show: false, sectionId: null, currentIcon: null });
  };
  
  // Toggle section collapse
  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };
  
  // Collapse/Expand all sections
  const collapseAllSections = () => {
    const sectionIds = faqs.filter(f => f.type === 'section').map(f => f.id);
    setCollapsedSections(new Set(sectionIds));
  };
  
  const expandAllSections = () => {
    setCollapsedSections(new Set());
  };
  
  // Group FAQs by section for display
  const getGroupedFaqs = useCallback(() => {
    const groups = [];
    let currentSection = null;
    let currentFaqs = [];
    
    // FAQs before any section (orphans)
    const orphanFaqs = [];
    let foundFirstSection = false;
    
    faqs.forEach(item => {
      if (item.type === 'section') {
        foundFirstSection = true;
        if (currentSection) {
          groups.push({ section: currentSection, faqs: currentFaqs });
        }
        currentSection = item;
        currentFaqs = [];
      } else if (item.type === 'faq') {
        if (!foundFirstSection) {
          orphanFaqs.push(item);
        } else {
          currentFaqs.push(item);
        }
      }
    });
    
    // Push the last section
    if (currentSection) {
      groups.push({ section: currentSection, faqs: currentFaqs });
    }
    
    return { orphanFaqs, groups };
  }, [faqs]);
  
  // Get section number for a section ID
  const getSectionNumber = useCallback((sectionId) => {
    const { groups } = getGroupedFaqs();
    const index = groups.findIndex(g => g.section.id === sectionId);
    return index !== -1 ? `${index + 1}.0` : '';
  }, [getGroupedFaqs]);
  
  // Get all sections with their numbers
  const getSectionsWithNumbers = useCallback(() => {
    const { groups } = getGroupedFaqs();
    return groups.map((g, index) => ({
      id: g.section.id,
      title: g.section.title,
      number: `${index + 1}.0`
    }));
  }, [getGroupedFaqs]);

  // Fetch FAQs
  const fetchFaqs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/faqs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(response.data.faqs || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  // Auto-save helper
  const autoSave = async (faqId, updates) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/super-admin/faqs/${faqId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('Saved');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Error saving:', err);
      setError('Failed to save changes');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Handle inline edit start
  const startEdit = (id, field, currentValue) => {
    setEditingId(id);
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  // Handle inline edit save
  const saveEdit = async () => {
    if (!editingId || !editingField) return;
    
    const updates = { [editingField]: editValue };
    
    // Update local state immediately
    setFaqs(prev => prev.map(faq => 
      faq.id === editingId ? { ...faq, [editingField]: editValue } : faq
    ));
    
    // Auto-save to backend
    await autoSave(editingId, updates);
    
    // Clear editing state
    setEditingId(null);
    setEditingField(null);
    setEditValue('');
  };

  // Handle checkbox change (auto-save)
  const handleCheckboxChange = async (faqId, field, newValue) => {
    // Update local state immediately
    setFaqs(prev => prev.map(faq => 
      faq.id === faqId ? { ...faq, [field]: newValue } : faq
    ));
    
    // Auto-save to backend
    await autoSave(faqId, { [field]: newValue });
  };

  // Helper to reorder FAQs on server
  const reorderFaqsOnServer = async (reorderedFaqs) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/super-admin/faqs/reorder`, {
        items: reorderedFaqs.map(faq => ({ id: faq.id, order: faq.order }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Error reordering:', err);
    }
  };

  // Add new FAQ
  const addFaq = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Determine insert position based on focused row
      let insertAfterOrder = null;
      if (focusedRowId) {
        const focusedItem = faqs.find(f => f.id === focusedRowId);
        if (focusedItem) {
          insertAfterOrder = focusedItem.order;
        }
      }
      
      const response = await axios.post(`${API}/super-admin/faqs`, {
        type: 'faq',
        question: 'New Question',
        answer: 'New Answer',
        show_web: false,
        show_admin_app: false,
        show_team_app: false,
        is_featured: false,
        is_popular: false,
        insert_after_order: insertAfterOrder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refetch to get correct order values
      await fetchFaqs();
      
      setSuccessMessage('FAQ added');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Error adding FAQ:', err);
      setError('Failed to add FAQ');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Add new Section Header with automatic child FAQ
  const addSection = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Determine insert position based on focused row
      let insertAfterOrder = null;
      if (focusedRowId) {
        const focusedItem = faqs.find(f => f.id === focusedRowId);
        if (focusedItem) {
          insertAfterOrder = focusedItem.order;
        }
      }
      
      // Create section (will be inserted at position, or appended if no position)
      const sectionResponse = await axios.post(`${API}/super-admin/faqs`, {
        type: 'section',
        title: 'New Section',
        show_web: false,
        show_admin_app: false,
        show_team_app: false,
        is_featured: false,
        is_popular: false,
        insert_after_order: insertAfterOrder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newSection = sectionResponse.data.item;
      
      // Create child FAQ immediately after the section
      await axios.post(`${API}/super-admin/faqs`, {
        type: 'faq',
        question: 'New Question',
        answer: 'New Answer',
        show_web: false,
        show_admin_app: false,
        show_team_app: false,
        is_featured: false,
        is_popular: false,
        insert_after_order: newSection.order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refetch to get correct order values
      await fetchFaqs();
      
      setSuccessMessage('Section added');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Error adding section:', err);
      setError('Failed to add section');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirm = (faq) => {
    const isSection = faq.type === 'section';
    let childCount = 0;
    
    if (isSection) {
      const { groups } = getGroupedFaqs();
      const sectionGroup = groups.find(g => g.section.id === faq.id);
      childCount = sectionGroup?.faqs.length || 0;
    }
    
    const faqName = isSection 
      ? faq.title || 'this section'
      : faq.question || 'this FAQ';
    
    setDeleteModal({ 
      show: true, 
      faqId: faq.id, 
      faqName, 
      isDeleting: false,
      isSection,
      childCount
    });
  };

  // Delete item (called from modal confirm)
  const deleteItem = async () => {
    const faqId = deleteModal.faqId;
    if (!faqId) return;
    
    setDeleteModal(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const token = localStorage.getItem('token');
      const itemToDelete = faqs.find(f => f.id === faqId);
      
      if (itemToDelete?.type === 'section') {
        // Delete section and all its FAQs
        const { groups } = getGroupedFaqs();
        const sectionGroup = groups.find(g => g.section.id === faqId);
        const idsToDelete = [faqId, ...(sectionGroup?.faqs.map(f => f.id) || [])];
        
        // Delete all items
        await Promise.all(idsToDelete.map(id => 
          axios.delete(`${API}/super-admin/faqs/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ));
        
        setFaqs(prev => prev.filter(faq => !idsToDelete.includes(faq.id)));
      } else {
        // Delete single FAQ
        await axios.delete(`${API}/super-admin/faqs/${faqId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check if this was the last FAQ in its section
        const faqIndex = faqs.findIndex(f => f.id === faqId);
        let parentSectionId = null;
        for (let i = faqIndex - 1; i >= 0; i--) {
          if (faqs[i].type === 'section') {
            parentSectionId = faqs[i].id;
            break;
          }
        }
        
        if (parentSectionId) {
          const { groups } = getGroupedFaqs();
          const sectionGroup = groups.find(g => g.section.id === parentSectionId);
          
          if (sectionGroup && sectionGroup.faqs.length === 1) {
            // This is the last FAQ in the section, create a new one
            const response = await axios.post(`${API}/super-admin/faqs`, {
              type: 'faq',
              question: 'New Question',
              answer: 'New Answer',
              show_web: false,
              show_admin_app: false,
              show_team_app: false,
              is_featured: false,
              is_popular: false
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            const newFaq = response.data.item;
            
            // Remove old FAQ and add new one in its place
            const newFaqs = faqs.filter(f => f.id !== faqId);
            const sectionIndex = newFaqs.findIndex(f => f.id === parentSectionId);
            newFaqs.splice(sectionIndex + 1, 0, newFaq);
            
            const reorderedFaqs = newFaqs.map((faq, index) => ({ ...faq, order: index }));
            setFaqs(reorderedFaqs);
            await reorderFaqsOnServer(reorderedFaqs);
            
            setDeleteModal({ show: false, faqId: null, faqName: '', isDeleting: false, isSection: false, childCount: 0 });
            setSuccessMessage('FAQ deleted (new placeholder created)');
            setTimeout(() => setSuccessMessage(''), 2000);
            return;
          }
        }
        
        setFaqs(prev => prev.filter(faq => faq.id !== faqId));
      }
      
      setDeleteModal({ show: false, faqId: null, faqName: '', isDeleting: false, isSection: false, childCount: 0 });
      setSuccessMessage('Item deleted');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Error deleting item:', err);
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
      setError('Failed to delete item');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Move section up
  const moveSectionUp = async (sectionId) => {
    const { groups } = getGroupedFaqs();
    const sectionIndex = groups.findIndex(g => g.section.id === sectionId);
    
    if (sectionIndex <= 0) return; // Already at top
    
    // Swap with previous section
    const currentGroup = groups[sectionIndex];
    const prevGroup = groups[sectionIndex - 1];
    
    // Build new faqs array
    const newGroups = [...groups];
    newGroups[sectionIndex - 1] = currentGroup;
    newGroups[sectionIndex] = prevGroup;
    
    // Flatten back to array
    const newFaqs = newGroups.flatMap(g => [g.section, ...g.faqs]);
    const reorderedFaqs = newFaqs.map((faq, index) => ({ ...faq, order: index }));
    
    setFaqs(reorderedFaqs);
    await reorderFaqsOnServer(reorderedFaqs);
    setSuccessMessage('Section moved up');
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  // Move section down
  const moveSectionDown = async (sectionId) => {
    const { groups } = getGroupedFaqs();
    const sectionIndex = groups.findIndex(g => g.section.id === sectionId);
    
    if (sectionIndex < 0 || sectionIndex >= groups.length - 1) return; // Already at bottom
    
    // Swap with next section
    const currentGroup = groups[sectionIndex];
    const nextGroup = groups[sectionIndex + 1];
    
    // Build new faqs array
    const newGroups = [...groups];
    newGroups[sectionIndex] = nextGroup;
    newGroups[sectionIndex + 1] = currentGroup;
    
    // Flatten back to array
    const newFaqs = newGroups.flatMap(g => [g.section, ...g.faqs]);
    const reorderedFaqs = newFaqs.map((faq, index) => ({ ...faq, order: index }));
    
    setFaqs(reorderedFaqs);
    await reorderFaqsOnServer(reorderedFaqs);
    setSuccessMessage('Section moved down');
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  // Move FAQ up within section
  const moveFaqUp = async (faqId) => {
    const faqIndex = faqs.findIndex(f => f.id === faqId);
    if (faqIndex <= 0) return;
    
    // Find parent section
    let parentSectionIndex = -1;
    for (let i = faqIndex - 1; i >= 0; i--) {
      if (faqs[i].type === 'section') {
        parentSectionIndex = i;
        break;
      }
    }
    
    // Check if prev item is a FAQ (not a section)
    const prevItem = faqs[faqIndex - 1];
    if (prevItem.type === 'section') return; // Can't move above section header
    
    // Swap
    const newFaqs = [...faqs];
    [newFaqs[faqIndex - 1], newFaqs[faqIndex]] = [newFaqs[faqIndex], newFaqs[faqIndex - 1]];
    
    const reorderedFaqs = newFaqs.map((faq, index) => ({ ...faq, order: index }));
    setFaqs(reorderedFaqs);
    await reorderFaqsOnServer(reorderedFaqs);
    setSuccessMessage('FAQ moved up');
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  // Move FAQ down within section
  const moveFaqDown = async (faqId) => {
    const faqIndex = faqs.findIndex(f => f.id === faqId);
    if (faqIndex < 0 || faqIndex >= faqs.length - 1) return;
    
    // Check if next item is a FAQ (not a section)
    const nextItem = faqs[faqIndex + 1];
    if (nextItem.type === 'section') return; // Can't move below into next section
    
    // Swap
    const newFaqs = [...faqs];
    [newFaqs[faqIndex], newFaqs[faqIndex + 1]] = [newFaqs[faqIndex + 1], newFaqs[faqIndex]];
    
    const reorderedFaqs = newFaqs.map((faq, index) => ({ ...faq, order: index }));
    setFaqs(reorderedFaqs);
    await reorderFaqsOnServer(reorderedFaqs);
    setSuccessMessage('FAQ moved down');
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  // Check if FAQ can move up
  const canMoveFaqUp = (faqId) => {
    const faqIndex = faqs.findIndex(f => f.id === faqId);
    if (faqIndex <= 0) return false;
    return faqs[faqIndex - 1].type === 'faq';
  };

  // Check if FAQ can move down
  const canMoveFaqDown = (faqId) => {
    const faqIndex = faqs.findIndex(f => f.id === faqId);
    if (faqIndex < 0 || faqIndex >= faqs.length - 1) return false;
    return faqs[faqIndex + 1].type === 'faq';
  };

  // Open move to section modal
  const openMoveModal = (faqId) => {
    setMoveModal({ show: true, faqId, targetSection: '', isMoving: false });
  };

  // Move FAQ to different section
  const moveFaqToSection = async () => {
    const { faqId, targetSection } = moveModal;
    if (!faqId || !targetSection) return;
    
    // Parse section number
    const sectionNum = parseFloat(targetSection);
    if (isNaN(sectionNum) || sectionNum < 1) {
      setError('Invalid section number');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    const { groups } = getGroupedFaqs();
    const targetIndex = Math.floor(sectionNum) - 1;
    
    if (targetIndex < 0 || targetIndex >= groups.length) {
      setError(`Section ${targetSection} does not exist`);
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    const targetGroup = groups[targetIndex];
    const faqToMove = faqs.find(f => f.id === faqId);
    
    if (!faqToMove) return;
    
    // Check if moving to same section
    const currentSectionId = (() => {
      const faqIdx = faqs.findIndex(f => f.id === faqId);
      for (let i = faqIdx - 1; i >= 0; i--) {
        if (faqs[i].type === 'section') return faqs[i].id;
      }
      return null;
    })();
    
    if (currentSectionId === targetGroup.section.id) {
      setError('FAQ is already in this section');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    setMoveModal(prev => ({ ...prev, isMoving: true }));
    
    try {
      // Check if this is the last FAQ in current section
      const currentGroup = groups.find(g => g.section.id === currentSectionId);
      const isLastInSection = currentGroup && currentGroup.faqs.length === 1;
      
      // Remove FAQ from current position
      let newFaqs = faqs.filter(f => f.id !== faqId);
      
      // If it was the last FAQ, add a placeholder
      if (isLastInSection && currentSectionId) {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/super-admin/faqs`, {
          type: 'faq',
          question: 'New Question',
          answer: 'New Answer',
          show_web: false,
          show_admin_app: false,
          show_team_app: false,
          is_featured: false,
          is_popular: false
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const placeholderFaq = response.data.item;
        const sectionIdx = newFaqs.findIndex(f => f.id === currentSectionId);
        newFaqs.splice(sectionIdx + 1, 0, placeholderFaq);
      }
      
      // Find target section position and insert at end of that section
      const targetSectionIdx = newFaqs.findIndex(f => f.id === targetGroup.section.id);
      let insertIdx = targetSectionIdx + 1;
      
      // Find end of target section
      while (insertIdx < newFaqs.length && newFaqs[insertIdx].type === 'faq') {
        insertIdx++;
      }
      
      // Insert FAQ
      newFaqs.splice(insertIdx, 0, faqToMove);
      
      const reorderedFaqs = newFaqs.map((faq, index) => ({ ...faq, order: index }));
      setFaqs(reorderedFaqs);
      await reorderFaqsOnServer(reorderedFaqs);
      
      setMoveModal({ show: false, faqId: null, targetSection: '', isMoving: false });
      setSuccessMessage(`FAQ moved to section ${targetSection}`);
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Error moving FAQ:', err);
      setMoveModal(prev => ({ ...prev, isMoving: false }));
      setError('Failed to move FAQ');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Render editable cell
  const renderEditableCell = (faq, field, value, isTextarea = false) => {
    const isEditing = editingId === faq.id && editingField === field;
    
    if (isEditing) {
      return isTextarea ? (
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setEditingId(null);
              setEditingField(null);
            }
          }}
          autoFocus
          className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[60px] resize-y"
          rows={3}
        />
      ) : (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEdit();
            if (e.key === 'Escape') {
              setEditingId(null);
              setEditingField(null);
            }
          }}
          autoFocus
          className="w-full px-2 py-1 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      );
    }
    
    return (
      <div
        onClick={() => startEdit(faq.id, field, value)}
        className="cursor-pointer hover:bg-gray-50 px-2 py-1 rounded min-h-[28px] text-sm text-gray-700"
        title="Click to edit"
      >
        {value || <span className="text-gray-400 italic">Click to edit...</span>}
      </div>
    );
  };

  // Build visible items with section numbers
  const visibleItems = React.useMemo(() => {
    const items = [];
    let currentSectionId = null;
    let sectionNumber = 0;
    const { groups } = getGroupedFaqs();
    
    faqs.forEach((faq) => {
      if (faq.type === 'section') {
        sectionNumber++;
        currentSectionId = faq.id;
        const sectionGroup = groups.find(g => g.section.id === faq.id);
        const faqCount = sectionGroup?.faqs.length || 0;
        const isCollapsed = collapsedSections.has(faq.id);
        const isFirstSection = sectionNumber === 1;
        const isLastSection = sectionNumber === groups.length;
        
        items.push({ 
          ...faq, 
          faqCount, 
          isCollapsed, 
          isSection: true,
          sectionNumber: `${sectionNumber}.0`,
          isFirstSection,
          isLastSection
        });
      } else if (faq.type === 'faq') {
        if (!currentSectionId || !collapsedSections.has(currentSectionId)) {
          items.push({ ...faq, isSection: false });
        }
      }
    });
    
    return items;
  }, [faqs, collapsedSections, getGroupedFaqs]);

  // Calculate counts
  const counts = {
    sections: faqs.filter(f => f.type === 'section').length,
    faqs: faqs.filter(f => f.type === 'faq').length,
    web: faqs.filter(f => f.show_web).length,
    adminApp: faqs.filter(f => f.show_admin_app).length,
    teamApp: faqs.filter(f => f.show_team_app).length,
    featured: faqs.filter(f => f.is_featured).length,
    popular: faqs.filter(f => f.is_popular).length,
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600 text-lg">Loading FAQs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white mb-8 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">FAQ Management</h1>
              <p className="text-purple-200 text-sm">Manage FAQs displayed across Web, Admin App, and Team App</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {saving && (
              <span className="text-sm text-purple-200 flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            )}
            {successMessage && (
              <span className="text-sm text-green-300 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {successMessage}
              </span>
            )}
            {error && (
              <span className="text-sm text-red-300">{error}</span>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
          <div className="text-2xl font-bold text-purple-600">{counts.sections}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Sections</div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
          <div className="text-2xl font-bold text-indigo-600">{counts.faqs}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">FAQs</div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{counts.web}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Web</div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
          <div className="text-2xl font-bold text-green-600">{counts.adminApp}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Admin App</div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
          <div className="text-2xl font-bold text-teal-600">{counts.teamApp}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Team App</div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
          <div className="text-2xl font-bold text-amber-500">{counts.featured}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Featured</div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow border border-gray-200">
          <div className="text-2xl font-bold text-rose-500">{counts.popular}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Popular</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex gap-3 flex-wrap items-center">
        <button
          onClick={addFaq}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white text-sm font-semibold rounded-lg hover:from-indigo-700 hover:to-indigo-600 transition-all shadow-lg hover:shadow-xl"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add FAQ
        </button>
        <button
          onClick={addSection}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-500 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          Add Section Header
        </button>
        
        {/* Collapse/Expand buttons */}
        <div className="flex gap-2 ml-4 border-l pl-4 border-gray-300">
          <button
            onClick={collapseAllSections}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            title="Collapse all sections"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            Collapse All
          </button>
          <button
            onClick={expandAllSections}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            title="Expand all sections"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            Expand All
          </button>
        </div>
        
        {focusedRowId && (
          <span className="ml-2 text-sm text-gray-500 flex items-center">
            <svg className="w-4 h-4 mr-1 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            New items will be inserted after selected row
          </span>
        )}
      </div>

      {/* FAQ Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-380px)] min-h-[400px]">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider w-20">Section</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ width: '25%' }}>Question / Section</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ width: '30%' }}>Answer</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-16">Web</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-20">Admin App</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-20">Team App</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-20">Featured</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-20">Popular</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {faqs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="font-medium">No FAQs yet</p>
                    <p className="text-sm mt-1">Click &quot;Add FAQ&quot; or &quot;Add Section Header&quot; to get started</p>
                  </td>
                </tr>
              ) : (
                visibleItems.map((faq) => (
                  <tr
                    key={faq.id}
                    onClick={() => setFocusedRowId(faq.id)}
                    className={`
                      ${faq.isSection 
                        ? 'bg-indigo-50 border-l-4 border-indigo-500' 
                        : 'bg-white'}
                      ${focusedRowId === faq.id && !faq.isSection ? 'ring-2 ring-indigo-500 ring-inset bg-indigo-50/50' : ''}
                      ${!faq.isSection ? 'hover:bg-gray-50' : 'hover:bg-indigo-100'} transition-colors cursor-pointer
                    `}
                  >
                    {/* Section Number / Collapse Toggle */}
                    <td className={`px-4 py-3 ${faq.isSection ? 'py-4' : ''}`}>
                      {faq.isSection ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSectionCollapse(faq.id);
                            }}
                            className="p-1 rounded hover:bg-indigo-200 transition-colors"
                            title={faq.isCollapsed ? 'Expand section' : 'Collapse section'}
                          >
                            <svg 
                              className={`w-4 h-4 text-indigo-600 transition-transform ${faq.isCollapsed ? '-rotate-90' : ''}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <span className="font-bold text-indigo-700">{faq.sectionNumber}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm pl-7">—</span>
                      )}
                    </td>
                    
                    {/* Question / Section Title */}
                    <td className={`px-4 ${faq.isSection ? 'py-4' : 'py-3'}`}>
                      {faq.isSection ? (
                        <div className="flex items-center gap-3">
                          {/* Icon Picker Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openIconPicker(faq.id, faq.icon);
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                            title="Click to change icon"
                          >
                            {(() => {
                              const IconComponent = getIconByName(faq.icon);
                              return <IconComponent className="w-4 h-4" />;
                            })()}
                          </button>
                          {editingId === faq.id && editingField === 'title' ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit();
                                if (e.key === 'Escape') {
                                  setEditingId(null);
                                  setEditingField(null);
                                }
                              }}
                              autoFocus
                              className="flex-1 px-2 py-1 text-sm font-semibold border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <>
                              <span className="font-semibold text-gray-900">{faq.title}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEdit(faq.id, 'title', faq.title);
                                }}
                                className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-100 rounded transition-colors"
                                title="Edit section title"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            </>
                          )}
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                            {faq.faqCount} FAQ{faq.faqCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ) : (
                        renderEditableCell(faq, 'question', faq.question)
                      )}
                    </td>
                    
                    {/* Answer */}
                    <td className={`px-4 ${faq.isSection ? 'py-4' : 'py-3'}`}>
                      {!faq.isSection ? (
                        renderEditableCell(faq, 'answer', faq.answer, true)
                      ) : (
                        <span className="text-gray-400 text-sm italic">—</span>
                      )}
                    </td>
                    
                    {/* Web Checkbox */}
                    <td className={`px-4 ${faq.isSection ? 'py-4' : 'py-3'} text-center`} onClick={(e) => e.stopPropagation()}>
                      {!faq.isSection && (
                        <input
                          type="checkbox"
                          checked={faq.show_web || false}
                          onChange={(e) => handleCheckboxChange(faq.id, 'show_web', e.target.checked)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                        />
                      )}
                    </td>
                    
                    {/* Admin App Checkbox */}
                    <td className={`px-4 ${faq.isSection ? 'py-4' : 'py-3'} text-center`} onClick={(e) => e.stopPropagation()}>
                      {!faq.isSection && (
                        <input
                          type="checkbox"
                          checked={faq.show_admin_app || false}
                          onChange={(e) => handleCheckboxChange(faq.id, 'show_admin_app', e.target.checked)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                        />
                      )}
                    </td>
                    
                    {/* Team App Checkbox */}
                    <td className={`px-4 ${faq.isSection ? 'py-4' : 'py-3'} text-center`} onClick={(e) => e.stopPropagation()}>
                      {!faq.isSection && (
                        <input
                          type="checkbox"
                          checked={faq.show_team_app || false}
                          onChange={(e) => handleCheckboxChange(faq.id, 'show_team_app', e.target.checked)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                        />
                      )}
                    </td>
                    
                    {/* Featured Checkbox */}
                    <td className={`px-4 ${faq.isSection ? 'py-4' : 'py-3'} text-center`} onClick={(e) => e.stopPropagation()}>
                      {!faq.isSection && (
                        <input
                          type="checkbox"
                          checked={faq.is_featured || false}
                          onChange={(e) => handleCheckboxChange(faq.id, 'is_featured', e.target.checked)}
                          className="w-4 h-4 border-gray-300 rounded cursor-pointer"
                          style={{ accentColor: '#f59e0b' }}
                        />
                      )}
                    </td>
                    
                    {/* Popular Checkbox */}
                    <td className={`px-4 ${faq.isSection ? 'py-4' : 'py-3'} text-center`} onClick={(e) => e.stopPropagation()}>
                      {!faq.isSection && (
                        <input
                          type="checkbox"
                          checked={faq.is_popular || false}
                          onChange={(e) => handleCheckboxChange(faq.id, 'is_popular', e.target.checked)}
                          className="w-4 h-4 border-gray-300 rounded cursor-pointer"
                          style={{ accentColor: '#f43f5e' }}
                        />
                      )}
                    </td>
                    
                    {/* Actions */}
                    <td className={`px-4 ${faq.isSection ? 'py-4' : 'py-3'} text-center`} onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        {faq.isSection ? (
                          <>
                            {/* Move Section Up */}
                            <button
                              onClick={() => moveSectionUp(faq.id)}
                              disabled={faq.isFirstSection}
                              className={`p-1.5 rounded transition-colors ${faq.isFirstSection ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                              title="Move section up"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </button>
                            {/* Move Section Down */}
                            <button
                              onClick={() => moveSectionDown(faq.id)}
                              disabled={faq.isLastSection}
                              className={`p-1.5 rounded transition-colors ${faq.isLastSection ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                              title="Move section down"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </>
                        ) : (
                          <>
                            {/* Move FAQ Up */}
                            <button
                              onClick={() => moveFaqUp(faq.id)}
                              disabled={!canMoveFaqUp(faq.id)}
                              className={`p-1.5 rounded transition-colors ${!canMoveFaqUp(faq.id) ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                              title="Move up within section"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </button>
                            {/* Move FAQ Down */}
                            <button
                              onClick={() => moveFaqDown(faq.id)}
                              disabled={!canMoveFaqDown(faq.id)}
                              className={`p-1.5 rounded transition-colors ${!canMoveFaqDown(faq.id) ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
                              title="Move down within section"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {/* Move to Section */}
                            <button
                              onClick={() => openMoveModal(faq.id)}
                              className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                              title="Move to different section"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                            </button>
                          </>
                        )}
                        {/* Delete */}
                        <button
                          onClick={() => showDeleteConfirm(faq)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-indigo-600">Tips:</span> 
          {' '}Click arrow to collapse/expand sections • Use up/down arrows to reorder • Click text to edit inline • Changes save automatically
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, faqId: null, faqName: '', isDeleting: false, isSection: false, childCount: 0 })}
        onConfirm={deleteItem}
        title={deleteModal.isSection ? "Delete Section" : "Delete FAQ Item"}
        message={
          deleteModal.isSection 
            ? `Are you sure you want to delete "${deleteModal.faqName}"?\n\nThis will also delete all ${deleteModal.childCount} FAQ(s) within this section.\n\nThis action cannot be undone.`
            : `Are you sure you want to delete "${deleteModal.faqName}"?\n\nThis action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteModal.isDeleting}
      />

      {/* Move to Section Modal */}
      {moveModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setMoveModal({ show: false, faqId: null, targetSection: '', isMoving: false })} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Move FAQ to Section</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter section number (e.g., 1.0, 2.0):
                </label>
                <input
                  type="text"
                  value={moveModal.targetSection}
                  onChange={(e) => setMoveModal(prev => ({ ...prev, targetSection: e.target.value }))}
                  placeholder="e.g., 2.0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  autoFocus
                />
              </div>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Available sections:</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {getSectionsWithNumbers().map(section => (
                    <div 
                      key={section.id} 
                      className="text-sm text-gray-600 cursor-pointer hover:bg-indigo-50 px-2 py-1 rounded"
                      onClick={() => setMoveModal(prev => ({ ...prev, targetSection: section.number }))}
                    >
                      <span className="font-medium text-indigo-600">{section.number}</span> - {section.title}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setMoveModal({ show: false, faqId: null, targetSection: '', isMoving: false })}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={moveFaqToSection}
                  disabled={!moveModal.targetSection || moveModal.isMoving}
                  className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {moveModal.isMoving && (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Move
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Icon Picker Modal */}
      {iconModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setIconModal({ show: false, sectionId: null, currentIcon: null })} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Section Icon</h3>
              
              <div className="grid grid-cols-6 gap-3 mb-6">
                {AVAILABLE_ICONS.map(({ name, icon: IconComponent, label }) => (
                  <button
                    key={name}
                    onClick={() => saveIcon(name)}
                    className={`
                      flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all
                      ${iconModal.currentIcon === name 
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-600' 
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50 text-gray-600'}
                    `}
                    title={label}
                  >
                    <IconComponent className="w-6 h-6" />
                    <span className="text-xs mt-1 truncate w-full text-center">{label}</span>
                  </button>
                ))}
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setIconModal({ show: false, sectionId: null, currentIcon: null })}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQManagementPage;
